import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { ReactElement } from 'react';
import { Accordion, Card, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useComponent } from '../../UserSetting/UsersLibrary/UserRights';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { toasterMsg } from '../../../Components/Toaster/Toaster';
import ConfirmationPopup from '../../../Components/ModalPopup/ConfirmationPopup';
import CustomTooltip from '../../../Components/Tooltip/CustomTooltip';
import Loader from '../../../Components/Utility/Loader/Loader';
import { getBOMTypeDetails } from '../Store/SystemMasters.action';
import {
  deleteSubBomCustomAttribute,
  getSubBomCustomAttributes,
  saveSubBomCustomAttributes,
  setSubBomAttributeActiveState,
} from '../Store/sub-bom-custom-attribute.action';
import SubBomTextCard from './SubBomTextCard';
import SubBomDropdownCard from './SubBomDropdownCard';
import type { SubBomAttribute, SubBomAttributeType, SubBomOptionRef } from './types';

interface BomTypeOption {
  _id: string;
  name: string;
}

interface AttributeTypeOption {
  name: string;
  value: SubBomAttributeType;
}

interface SubBomRootState {
  SystemMastersDetails?: {
    BOMTypeList?: BomTypeOption[];
    customAttributeSubBomList?: SubBomAttribute[];
  };
  Users?: { logInRights?: unknown };
}

interface SubBomParentState {
  rearrangeModal?: string;
  rearrangeData?: Array<{ name: string; _id: string }>;
}

interface CustomAttributeSubBomMasterProps {
  parentState?: SubBomParentState;
  parentSetState?: (updater: (previous: SubBomParentState) => SubBomParentState) => void;
  OpenReArrangeFunction?: (masterName: string) => void;
}

interface PendingConfirmation {
  mode: 'delete' | 'active';
  attributeId: string;
  optionId: string | null;
  nextActive?: boolean;
}

interface ValidationResult {
  attributeErrors: Record<number, string>;
  optionErrors: Record<number, Record<number, string>>;
  hasError: boolean;
}

const TYPE_OPTIONS: AttributeTypeOption[] = [
  { name: 'Dropdown', value: 'dropdown' },
  { name: 'Text', value: 'text' },
  { name: 'Multi Dropdown', value: 'multi-dropdown' },
];

const OPTION_TYPES: SubBomAttributeType[] = ['dropdown', 'multi-dropdown'];

const SCROLL_TO_BOTTOM = 1000;

const ERROR_TEXT_REQUIRED = 'common:TEXT_CARD.TEXT_REQUIRED';
const ERROR_LABEL_REQUIRED = 'common:DROP_DOWN_CARD.LABEL_REQUIRED';
const ERROR_VALUE_REQUIRED = 'common:DROP_DOWN_CARD.VALUE_REQUIRED';
const ERROR_DUPLICATE_NAME = 'SUB_BOM_CUSTOM_ATTRIBUTE.DUPLICATE_NAME';
const ERROR_DUPLICATE_OPTION = 'SUB_BOM_CUSTOM_ATTRIBUTE.DUPLICATE_OPTION_NAME';

/**
 * Normalises a name for comparison, matching the rule the server enforces:
 * trimmed and case-folded.
 * @param {string | undefined} value - The raw name.
 * @returns {string} The comparison key.
 */
function comparisonKey(value: string | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/**
 * Validates the whole list in one linear pass, keyed by index. Attribute names
 * clash only within their own type and option names only within their own
 * attribute, which is exactly what the server enforces — so the inline errors
 * and the API rejections can never disagree.
 * @param {SubBomAttribute[]} attributes - The list being edited.
 * @returns {ValidationResult} The errors by index.
 */
export function validateAttributes(attributes: SubBomAttribute[]): ValidationResult {
  const attributeErrors: Record<number, string> = {};
  const optionErrors: Record<number, Record<number, string>> = {};
  const seenNames = new Set<string>();

  attributes.forEach((attribute, index) => {
    const name = comparisonKey(attribute.keyName);
    if (!name) {
      attributeErrors[index] = attribute.type === 'text' ? ERROR_TEXT_REQUIRED : ERROR_LABEL_REQUIRED;
    } else {
      const nameKey = `${name}::${attribute.type}`;
      if (seenNames.has(nameKey)) {
        attributeErrors[index] = ERROR_DUPLICATE_NAME;
      }
      seenNames.add(nameKey);
    }

    if (!OPTION_TYPES.includes(attribute.type)) {
      return;
    }
    const seenOptions = new Set<string>();
    (attribute.options ?? []).forEach((option, optionIndex) => {
      const optionName = comparisonKey(option.keyName);
      if (!optionName) {
        optionErrors[index] = { ...(optionErrors[index] ?? {}), [optionIndex]: ERROR_VALUE_REQUIRED };
        return;
      }
      if (seenOptions.has(optionName)) {
        optionErrors[index] = { ...(optionErrors[index] ?? {}), [optionIndex]: ERROR_DUPLICATE_OPTION };
      }
      seenOptions.add(optionName);
    });
  });

  return {
    attributeErrors,
    optionErrors,
    hasError: Object.keys(attributeErrors).length > 0 || Object.keys(optionErrors).length > 0,
  };
}

/**
 * Projects the edited list onto the save payload. Order is preserved because
 * the server assigns `seqNumber` from it, and a `text` attribute is stripped of
 * options so a type change cannot smuggle stale ones through.
 *
 * `isActive` is never sent. This screen has no control for it — it is owned by
 * the eye icon, which writes through its own endpoint — so echoing the copy
 * held in local state would let a save switch an attribute back on that someone
 * else had just switched off.
 * @param {SubBomAttribute[]} attributes - The list being edited.
 * @returns {SubBomAttribute[]} The payload.
 */
export function toSavePayload(attributes: SubBomAttribute[]): SubBomAttribute[] {
  return attributes.map((attribute) => ({
    _id: attribute._id ?? null,
    keyName: (attribute.keyName ?? '').trim(),
    type: attribute.type,
    textFieldConfig: attribute.type === 'text' ? attribute.textFieldConfig ?? {} : null,
    options: OPTION_TYPES.includes(attribute.type)
      ? (attribute.options ?? []).map((option) => ({
          _id: option._id ?? null,
          keyName: (option.keyName ?? '').trim(),
        }))
      : [],
  }));
}

/**
 * Applies a confirmed delete or active switch to the list already on screen.
 *
 * The change is mirrored locally rather than re-fetched, because a re-fetch
 * replaces the whole list and would throw away any rows the user has added or
 * renamed but not yet saved.
 * @param {SubBomAttribute[]} attributes - The list being edited.
 * @param {'delete' | 'active'} mode - Which change was confirmed.
 * @param {string} attributeId - The attribute the change landed on.
 * @param {string | null} optionId - The option it was narrowed to, when it was.
 * @param {boolean} nextActive - The state an active switch applied.
 * @returns {SubBomAttribute[]} The updated list.
 */
function applyConfirmedChange(
  attributes: SubBomAttribute[],
  mode: 'delete' | 'active',
  attributeId: string,
  optionId: string | null,
  nextActive: boolean,
): SubBomAttribute[] {
  if (mode === 'delete' && !optionId) {
    return attributes.filter((attribute) => attribute._id !== attributeId);
  }
  return attributes.map((attribute) => {
    if (attribute._id !== attributeId) {
      return attribute;
    }
    if (!optionId) {
      return { ...attribute, isActive: nextActive };
    }
    const options = attribute.options ?? [];
    return {
      ...attribute,
      options:
        mode === 'delete'
          ? options.filter((option) => option._id !== optionId)
          : options.map((option) => (option._id === optionId ? { ...option, isActive: nextActive } : option)),
    };
  });
}

/**
 * Sub-BOM custom attributes master. Defines the attributes a sub-BOM row can
 * carry, scoped to one BOM Type.
 *
 * The whole list is held in state across all three types and only filtered for
 * display, because the server assigns display order from the payload order and
 * needs one total order per BOM Type — a type-filtered save would renumber the
 * types against each other.
 * @param {CustomAttributeSubBomMasterProps} props - Provided by the System Masters page for the reorder modal.
 * @returns {ReactElement} The rendered accordion.
 */
function CustomAttributeSubBomMaster({
  parentState,
  parentSetState,
  OpenReArrangeFunction,
}: CustomAttributeSubBomMasterProps): ReactElement {
  const { t } = useTranslation(['systemMaster', 'common']);
  const { CheckActionPermission } = useComponent() as unknown as {
    CheckActionPermission: (rights: unknown, module: string, section: string, action: string) => boolean;
  };
  const dispatch = useAppDispatch();

  const bomTypeList = useSelector((root: SubBomRootState) => root.SystemMastersDetails?.BOMTypeList ?? []);
  const storedAttributes = useSelector((root: SubBomRootState) => root.SystemMastersDetails?.customAttributeSubBomList);
  const logInRights = useSelector((root: SubBomRootState) => root.Users?.logInRights);

  const [selectedBomType, setSelectedBomType] = useState<BomTypeOption | null>(null);
  const [attributes, setAttributes] = useState<SubBomAttribute[]>([]);
  const [activeType, setActiveType] = useState<AttributeTypeOption>(TYPE_OPTIONS[0] as AttributeTypeOption);
  const [openList, setOpenList] = useState<number[]>([]);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [confirmation, setConfirmation] = useState<PendingConfirmation | null>(null);
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);
  const wasRearrangingRef = useRef(false);

  useEffect(() => {
    if (scrollEl) {
      scrollEl.scrollTop = SCROLL_TO_BOTTOM;
    }
  }, [scrollEl]);

  const canEdit = useMemo<boolean>(
    () => Boolean(CheckActionPermission(logInRights, 'masters', 'customAttribute', 'addEdit')),
    [CheckActionPermission, logInRights],
  );
  const canDelete = useMemo<boolean>(
    () => Boolean(CheckActionPermission(logInRights, 'masters', 'customAttribute', 'delete')),
    [CheckActionPermission, logInRights],
  );

  const validation = useMemo(() => validateAttributes(attributes), [attributes]);

  const visibleErrors = useMemo<ValidationResult>(
    () => (showErrors ? validation : { attributeErrors: {}, optionErrors: {}, hasError: validation.hasError }),
    [showErrors, validation],
  );

  const visibleCards = useMemo(
    () =>
      attributes
        .map((attribute, index) => ({ attribute, index }))
        .filter((entry) => entry.attribute.type === activeType.value),
    [attributes, activeType],
  );

  useEffect(() => {
    const stored = Array.isArray(storedAttributes) ? storedAttributes : [];
    const bomTypeId = selectedBomType?._id;
    setAttributes(bomTypeId ? stored.filter((attribute) => attribute.bomTypeId === bomTypeId) : []);
  }, [storedAttributes, selectedBomType]);

  const loadAttributes = useCallback(
    async (bomTypeId: string | undefined): Promise<void> => {
      if (!bomTypeId) {
        return;
      }
      setIsLoading(true);
      await dispatch(getSubBomCustomAttributes(bomTypeId));
      setIsLoading(false);
    },
    [dispatch],
  );

  useEffect(() => {
    if (selectedBomType?._id) {
      loadAttributes(selectedBomType._id);
    }
  }, [selectedBomType, loadAttributes]);

  const toggleAccordion = useCallback(async (): Promise<void> => {
    const nextOpen = !isAccordionOpen;
    setIsAccordionOpen(nextOpen);
    if (nextOpen) {
      setIsLoading(true);
      await dispatch(getBOMTypeDetails({ isAllRecord: true }));
      setIsLoading(false);
    }
  }, [dispatch, isAccordionOpen]);

  const handleBomTypeChange = useCallback((value: BomTypeOption | null): void => {
    setSelectedBomType(value);
    setOpenList([]);
    setShowErrors(false);
  }, []);

  const handleTypeFilterChange = useCallback((value: AttributeTypeOption | null): void => {
    if (value) {
      setActiveType(value);
    }
  }, []);

  const handleAddAttribute = useCallback((): void => {
    if (!selectedBomType?._id) {
      toasterMsg(t('common:BOM_TYPE_MASTER.SELECT_BOM_TYPE'), 'danger');
      return;
    }
    setAttributes((previous) => [
      ...previous,
      {
        _id: null,
        bomTypeId: selectedBomType._id,
        keyName: '',
        type: activeType.value,
        isActive: true,
        textFieldConfig: null,
        options: [],
      },
    ]);
  }, [activeType, selectedBomType, t]);

  const handleNameChange = useCallback((index: number, value: string): void => {
    setAttributes((previous) => previous.map((item, position) => (position === index ? { ...item, keyName: value } : item)));
  }, []);

  const handleAddOption = useCallback((index: number): void => {
    setAttributes((previous) =>
      previous.map((item, position) =>
        position === index ? { ...item, options: [...(item.options ?? []), { _id: null, keyName: '', isActive: true }] } : item,
      ),
    );
    setOpenList((previous) => (previous.includes(index) ? previous : [...previous, index]));
  }, []);

  const handleOptionNameChange = useCallback((index: number, optionIndex: number, value: string): void => {
    setAttributes((previous) =>
      previous.map((item, position) =>
        position === index
          ? {
              ...item,
              options: (item.options ?? []).map((option, optionPosition) =>
                optionPosition === optionIndex ? { ...option, keyName: value } : option,
              ),
            }
          : item,
      ),
    );
  }, []);

  const handleToggleOpen = useCallback((index: number): void => {
    setOpenList((previous) => (previous.includes(index) ? previous.filter((entry) => entry !== index) : [...previous, index]));
  }, []);

  const handleToggleActive = useCallback((attributeId: string, optionId: string | null, nextActive: boolean): void => {
    setConfirmation({ mode: 'active', attributeId, optionId, nextActive });
  }, []);

  const handleDelete = useCallback((index: number, attributeId: string | null, optionRef: SubBomOptionRef | null): void => {
    if (!attributeId || (optionRef && !optionRef.optionId)) {
      setAttributes((previous) => {
        if (!optionRef) {
          return previous.filter((_, position) => position !== index);
        }
        return previous.map((item, position) =>
          position === index
            ? { ...item, options: (item.options ?? []).filter((_, optionPosition) => optionPosition !== optionRef.optionIndex) }
            : item,
        );
      });
      return;
    }
    setConfirmation({ mode: 'delete', attributeId, optionId: optionRef?.optionId ?? null });
  }, []);

  const closeConfirmation = useCallback((): void => {
    setConfirmation(null);
    setIsConfirming(false);
  }, []);

  const confirmAction = useCallback(async (): Promise<void> => {
    if (!confirmation) {
      return;
    }
    const { mode, attributeId, optionId, nextActive } = confirmation;
    setIsConfirming(true);
    const succeeded =
      mode === 'delete'
        ? await dispatch(deleteSubBomCustomAttribute(attributeId, optionId ? [optionId] : [], t))
        : await dispatch(setSubBomAttributeActiveState(attributeId, Boolean(nextActive), optionId, t));

    if (succeeded) {
      setAttributes((previous) => applyConfirmedChange(previous, mode, attributeId, optionId, Boolean(nextActive)));
    }
    closeConfirmation();
  }, [confirmation, dispatch, t, closeConfirmation]);

  const handleSave = useCallback(async (): Promise<void> => {
    if (!selectedBomType?._id) {
      toasterMsg(t('common:BOM_TYPE_MASTER.SELECT_BOM_TYPE'), 'danger');
      return;
    }
    if (attributes.length === 0) {
      toasterMsg(t('SUB_BOM_CUSTOM_ATTRIBUTE.NOTHING_TO_SAVE'), 'danger');
      return;
    }
    if (validation.hasError) {
      setShowErrors(true);
      toasterMsg(t('SUB_BOM_CUSTOM_ATTRIBUTE.FIX_ERRORS'), 'danger');
      return;
    }
    setShowErrors(false);
    setIsSaving(true);
    await dispatch(saveSubBomCustomAttributes(selectedBomType._id, toSavePayload(attributes), t));
    setIsSaving(false);
  }, [attributes, dispatch, selectedBomType, t, validation.hasError]);

  useEffect(() => {
    if (parentState?.rearrangeModal !== 'subBomCustomAttribute' || !parentSetState) {
      return;
    }
    parentSetState((previous) => ({
      ...previous,
      rearrangeData: attributes
        .filter((attribute): attribute is SubBomAttribute & { _id: string } => Boolean(attribute._id))
        .map((attribute) => ({ name: attribute.keyName, _id: attribute._id })),
    }));
  }, [attributes, parentState?.rearrangeModal, parentSetState]);

  useEffect(() => {
    if (parentState?.rearrangeModal === 'subBomCustomAttribute') {
      wasRearrangingRef.current = true;
      return;
    }
    if (wasRearrangingRef.current) {
      wasRearrangingRef.current = false;
      loadAttributes(selectedBomType?._id);
    }
  }, [parentState?.rearrangeModal, loadAttributes, selectedBomType]);

  const openRearrange = useCallback((): void => {
    OpenReArrangeFunction?.('subBomCustomAttribute');
  }, [OpenReArrangeFunction]);

  const canRearrange = attributes.some((attribute) => Boolean(attribute._id));

  return (
    <>
      <Accordion className="mb-4 master-accordian custom-attribute-master">
        <Card>
          <Card.Header>
            <div className="title-container">
              <p>{t('SUB_BOM_CUSTOM_ATTRIBUTE.TITLE')}</p>
            </div>
            <div className={!canEdit ? 'action-title-btns single-btn' : 'action-title-btns'}>
              {canEdit && (
                <CustomTooltip tooltipText={t('common:CUSTOM_ATTRIBUTES.REARRANGE_TOOLTIP')} placement="left">
                  <button
                    type="button"
                    onClick={openRearrange}
                    className="btn d-inline-flex mr-16 p-0"
                    disabled={!canRearrange}
                    aria-label="Rearrange"
                  >
                    <span className="icon-Rearrange text-grey f-24" />
                  </button>
                </CustomTooltip>
              )}
              {canEdit && (
                <CustomTooltip tooltipText={t('SUB_BOM_CUSTOM_ATTRIBUTE.ADD')} placement="left">
                  <button type="button" onClick={handleAddAttribute} className="filter-btn filter-button_add">
                    <span className="text-icon">{t('common:APPROVE_REJECTS.ADD')}</span>
                    <i className="icon-add" />
                  </button>
                </CustomTooltip>
              )}
              <CustomTooltip
                placement="left"
                tooltipText={isAccordionOpen ? t('common:CUSTOM_ATTRIBUTES.CLOSE') : t('common:CUSTOM_ATTRIBUTES.OPEN')}
              >
                <button onClick={toggleAccordion} type="button" aria-label="Toggle section">
                  <i className={isAccordionOpen ? 'icon-new-up' : 'icon-new-down'} />
                </button>
              </CustomTooltip>
            </div>
          </Card.Header>

          {isAccordionOpen &&
            (isLoading ? (
              <Loader />
            ) : (
              <Card.Body>
                {canEdit && (
                  <div className="common-react-select select-container mt-3">
                    <Select
                      options={bomTypeList}
                      placeholder={t('common:BOM_TYPE_MASTER.SELECT_BOM_TYPE')}
                      getOptionLabel={(option: BomTypeOption) => `${option.name}`}
                      getOptionValue={(option: BomTypeOption) => `${option._id}`}
                      name="bomsubtype"
                      value={selectedBomType}
                      className="common-select"
                      classNamePrefix="common-select"
                      inputId="subBomAttributeBomType"
                      onChange={handleBomTypeChange}
                      styles={{ menuList: (base: Record<string, unknown>) => ({ ...base, maxHeight: 175, overflowY: 'scroll' }) }}
                    />
                  </div>
                )}
                {selectedBomType && (
                  <div className="common-react-select select-container">
                    <Select
                      options={TYPE_OPTIONS}
                      placeholder={t('common:CUSTOM_ATTRIBUTES.TYPE_SELECT_PLACEHOLDER')}
                      getOptionLabel={(option: AttributeTypeOption) => `${option.name}`}
                      getOptionValue={(option: AttributeTypeOption) => `${option.value}`}
                      name="type"
                      value={activeType}
                      className="common-select"
                      classNamePrefix="common-select"
                      inputId="subBomAttributeTypeFilter"
                      onChange={handleTypeFilterChange}
                    />
                  </div>
                )}

                <PerfectScrollbar containerRef={(ref: HTMLElement) => setScrollEl(ref)}>
                  {visibleCards.map(({ attribute, index }) =>
                    attribute.type === 'text' ? (
                      <SubBomTextCard
                        key={attribute._id ?? `new-text-${index}`}
                        item={attribute}
                        index={index}
                        error={visibleErrors.attributeErrors[index]}
                        onNameChange={handleNameChange}
                        onToggleActive={handleToggleActive}
                        onDelete={handleDelete}
                        canEdit={canEdit}
                        canDelete={canDelete}
                      />
                    ) : (
                      <SubBomDropdownCard
                        key={attribute._id ?? `new-dropdown-${index}`}
                        item={attribute}
                        index={index}
                        error={visibleErrors.attributeErrors[index]}
                        optionErrors={visibleErrors.optionErrors[index]}
                        isOpen={openList.includes(index)}
                        onToggleOpen={handleToggleOpen}
                        onNameChange={handleNameChange}
                        onAddOption={handleAddOption}
                        onOptionNameChange={handleOptionNameChange}
                        onToggleActive={handleToggleActive}
                        onDelete={handleDelete}
                        canEdit={canEdit}
                        canDelete={canDelete}
                      />
                    ),
                  )}
                </PerfectScrollbar>

                {canEdit && (
                  <div className="section-save">
                    <CustomTooltip tooltipText={t('common:COMMON_LABEL.SAVE')}>
                      <button
                        aria-label="Save"
                        type="button"
                        className="btn btn-page-filter btn-white ml-0"
                        onClick={handleSave}
                        disabled={isSaving}
                      >
                        {isSaving ? (
                          <Spinner animation="border" />
                        ) : (
                          <>
                            <span className="icon-Save c-button-align f-20 mr-2" /> {t('common:PRODUCT_IDENTIFIER.SAVE')}
                          </>
                        )}
                      </button>
                    </CustomTooltip>
                  </div>
                )}
              </Card.Body>
            ))}
        </Card>
      </Accordion>

      {confirmation && (
        <ConfirmationPopup
          onOpen
          onConfirm={confirmAction}
          toggle={closeConfirmation}
          loading={isConfirming}
          title={
            confirmation.mode === 'delete'
              ? t('common:CUSTOM_ATTRIBUTES.DELETE_CONFIRM_TITLE')
              : t('common:CUSTOM_ATTRIBUTES.ACTIVE_DEACTIVATE_CONFIRM_TITLE')
          }
          message={
            confirmation.mode === 'delete'
              ? t('common:CUSTOM_ATTRIBUTES.DELETE_CONFIRM_MESSAGE')
              : t('common:CUSTOM_ATTRIBUTES.ACTIVE_DEACTIVATE_CONFIRM_MESSAGE')
          }
        />
      )}
    </>
  );
}

export default CustomAttributeSubBomMaster;
